import { WebSocketEventType, WebSocketMessage } from 'services/websocket/events';
import { mutate } from 'swr';
import { MessageContent } from './types/messageContent';
import { ChannelType, ChatTabType, MessageStatus } from './types/types';

// Map chat tab types to API channel types
export const mapChatTypeToChannelType = (type: ChatTabType): ChannelType => {
	switch (type) {
		case ChatTabType.Direct:
			return ChannelType.DirectMessaging;
		case ChatTabType.Groups:
			return ChannelType.GroupMessaging;
		case ChatTabType.Completed:
			return ChannelType.DirectReadonly;
		default:
			return ChannelType.DirectMessaging;
	}
};

// Update SWR cache with WebSocket message
export const updateCacheWithSocketMessage = async (
	message: WebSocketMessage,
	currentUserId?: string
) => {
	switch (message.messageType) {
		case WebSocketEventType.NewMessage: {
			const {
				id,
				channel_id,
				content: contentJson,
				user_id,
				timestamp,
				author,
				attachments,
			} = message.payload;

			// Check if this message is from the current user
			const isOwnMessage = currentUserId && user_id === currentUserId;

			// Parse the content JSON
			let messageContents: MessageContent[] = [];
			try {
				const content = JSON.parse(contentJson);
				messageContents = content;
			} catch (error) {
				console.error('Error parsing message content:', error);
			}

			// Create new message object in API format (not Message class)
			// ChatWindow expects API format and will convert with convertApiMessageToLocalModel
			const apiMessage = {
				id,
				author,
				channelId: channel_id,
				content: contentJson,
				createdAt: timestamp,
				attachments: attachments || [],
				status: MessageStatus.Active,
			};

			// Update the infinite messages cache for the specified channel
			// This must use a more targeted approach to match useSWRInfinite's cache structure
			await mutate(
				(key) => {
					// Match any key that starts with 'messages' and contains the channel_id
					return Array.isArray(key) && key[0] === 'messages' && key[1] === channel_id;
				},
				(data: any) => {
					// If no data, create an array with the new message
					if (!data) {
						return [[apiMessage]];
					}

					// useSWRInfinite stores data as an array of pages, where each page is an array of messages
					// We need to add the message to the first page (most recent messages)
					if (Array.isArray(data)) {
						// Copy the data to avoid mutating the original
						const newData = [...data];

						// Get the first page, or create it if it doesn't exist
						if (newData.length === 0) {
							newData.push([]);
						}

						// Check if the message already exists to prevent duplicates
						const firstPage = newData[0];
						if (Array.isArray(firstPage)) {
							const exists = firstPage.some((msg) => msg.id === id);
							if (!exists) {
								// Add to the first page (most recent)
								newData[0] = [...firstPage, apiMessage];
							}
						} else {
							// Initialize the first page if it's not an array
							newData[0] = [apiMessage];
						}

						return newData;
					}

					return data;
				},
				{
					populateCache: true,
					revalidate: true, // This is important for useSWRInfinite
				}
			);

			// Update the channel's last message info
			const channelKey = ['channel', channel_id];
			await mutate(
				(key) => Array.isArray(key) && key[0] === channelKey[0] && key[1] === channelKey[1],
				(channel: any) => {
					if (!channel) {
						return channel;
					}
					return {
						...channel,
						lastMessageTimestamp: timestamp,
						lastMessageId: id,
					};
				},
				{ populateCache: true, revalidate: true }
			);

			// Update the channels list to reflect the new message
			const channelsKey = ['channels'];
			await mutate(
				(key) => Array.isArray(key) && key[0] === channelsKey[0],
				(data: any) => {
					if (!data) {
						return data;
					}

					// For infinite SWR with channels
					const updatedData = data.map((page: any) => {
						if (!Array.isArray(page)) return page;

						// First, update the channel with the new message info
						const updatedPage = page.map((chan: any) => {
							if (chan.id === channel_id) {
								return {
									...chan,
									lastMessageTimestamp: timestamp,
									lastMessageId: id,
									// Increment notification count if applicable
									notificationCount: (chan.notificationCount || 0) + 1,
								};
							}
							return chan;
						});

						// Then sort the channels by lastMessageTimestamp (descending)
						return updatedPage.sort((a: any, b: any) => {
							const timestampA = a.lastMessageTimestamp || 0;
							const timestampB = b.lastMessageTimestamp || 0;
							return timestampB - timestampA;
						});
					});

					return updatedData;
				},
				{ populateCache: true, revalidate: true }
			);

			// Also update the infinite channel pages if they exist
			await mutate(
				(key: any) => {
					// Match any key that looks like it's from useChannels hook
					return Array.isArray(key) && typeof key[0] === 'object' && key[0].hasOwnProperty('type'); // Channels requests have a type property
				},
				(data: any) => {
					if (!data || !Array.isArray(data)) {
						return data;
					}

					// Process each page of results
					const updatedPages = data.map((page) => {
						if (!Array.isArray(page)) return page;

						// Update the specific channel
						const updatedPage = page.map((chan) => {
							if (chan.id === channel_id) {
								return {
									...chan,
									lastMessageTimestamp: timestamp,
									lastMessageId: id,
									notificationCount: (chan.notificationCount || 0) + 1,
									lastMessage: {
										...(chan.lastMessage || {}),
										id,
										createdAt: timestamp,
									},
								};
							}
							return chan;
						});

						// Sort the page by most recent message
						return updatedPage.sort((a, b) => {
							const timestampA = a.lastMessageTimestamp || a.lastMessage?.createdAt || 0;
							const timestampB = b.lastMessageTimestamp || b.lastMessage?.createdAt || 0;
							return timestampB - timestampA;
						});
					});

					return updatedPages;
				},
				{ populateCache: true, revalidate: false }
			);

			break;
		}
		case WebSocketEventType.EditMessage: {
			// Extract payload data - ensure we have fallbacks for missing properties
			const payload = message.payload || {};
			const id = payload.id;
			const channel_id = payload.channel_id;

			// Skip if we don't have the essential data
			if (!id || !channel_id) {
				console.error('Missing required data for edit message event');
				break;
			}

			// Parse content JSON if available
			let messageContents: MessageContent[] = [];
			if (payload.content) {
				try {
					const content = JSON.parse(payload.content);
					messageContents = content;
				} catch (error) {
					console.error('Error parsing edited message content:', error);
				}
			}

			// Calculate edit timestamp if available
			const editTimestamp = payload.timestamp ? payload.timestamp * 1000 : Date.now();

			// Update the messages cache for the specified channel
			await mutate(
				(key) => {
					// Match any key that starts with 'messages' and contains the channel_id
					return Array.isArray(key) && key[0] === 'messages' && key[1] === channel_id;
				},
				(data: any) => {
					if (!data || !Array.isArray(data)) {
						return data;
					}

					// Create a deep copy of the data array
					const updatedData = [...data];

					// For infinite SWR, we need to find the message in all pages
					let found = false;
					for (let i = 0; i < updatedData.length; i++) {
						if (Array.isArray(updatedData[i])) {
							// Find the message within this page
							const updatedPage = [...updatedData[i]];
							for (let j = 0; j < updatedPage.length; j++) {
								if (updatedPage[j].id === id) {
									found = true;

									// Create a new object with the updated properties
									updatedPage[j] = {
										...updatedPage[j],
										contents:
											messageContents.length > 0 ? messageContents : updatedPage[j].contents,
										editDate: editTimestamp,
									};
								}
							}

							// Update the page in the data array
							updatedData[i] = updatedPage;

							if (found) break;
						}
					}

					return updatedData;
				},
				{ populateCache: true, revalidate: true }
			);

			break;
		}
		case WebSocketEventType.DeleteMessage: {
			// Extract payload data with fallbacks
			const payload = message.payload || {};
			const id = payload.id;
			const channel_id = payload.channel_id;

			// Skip if we don't have the essential data
			if (!id || !channel_id) {
				console.error('Missing required data for delete message event');
				break;
			}

			// Update the messages cache for the specified channel
			await mutate(
				(key) => {
					// Match any key that starts with 'messages' and contains the channel_id
					return Array.isArray(key) && key[0] === 'messages' && key[1] === channel_id;
				},
				(data: any) => {
					if (!data || !Array.isArray(data)) {
						return data;
					}

					// Create a deep copy of the data array
					const updatedData = [...data];

					// For infinite SWR, we need to find the message in all pages
					let found = false;
					for (let i = 0; i < updatedData.length; i++) {
						if (Array.isArray(updatedData[i])) {
							// Find the message within this page
							const updatedPage = [...updatedData[i]];
							for (let j = 0; j < updatedPage.length; j++) {
								if (updatedPage[j].id === id) {
									found = true;

									// Mark the message as deleted instead of removing it
									// This preserves the conversation flow
									updatedPage[j] = {
										...updatedPage[j],
										messageStatus: MessageStatus.Deleted,
									};
								}
							}

							// Update the page in the data array
							updatedData[i] = updatedPage;

							if (found) break;
						}
					}

					return updatedData;
				},
				{ populateCache: true, revalidate: true }
			);

			break;
		}
	}
};


